// src/components/EducationEditModal.jsx
import React, { useState } from "react";
import UniversityAutocomplete from "./UniversityAutocomplete";

export default function EducationEditModal({
  mode = "add", // 'add' | 'edit'
  education = null, // when editing: { educationId, institutionName, degree, fieldOfStudy, startYear, endYear }
  onClose,
  onSave, // (values) => Promise<void>
}) {
  const [form, setForm] = useState({
    institutionName: education?.institutionName || "",
    degree: education?.degree || "",
    fieldOfStudy: education?.fieldOfStudy || "",
    startYear: education?.startYear ?? "",
    endYear: education?.endYear ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      // Normalize numeric fields
      const payload = {
        ...form,
        startYear: form.startYear === "" ? null : Number(form.startYear),
        endYear: form.endYear === "" ? null : Number(form.endYear),
      };
      await onSave(payload);
    } catch (ex) {
      setErr(ex?.message || "Failed to save education record");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === "edit" ? "Edit Education" : "Add Education"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Institution with autocomplete */}
          <div>
            <span className="mb-1 block text-sm text-gray-700">
              Institution
            </span>
            <UniversityAutocomplete
              value={form.institutionName}
              onChange={(val) =>
                setForm((s) => ({ ...s, institutionName: val }))
              }
              placeholder="Start typing a university…"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-gray-700">Degree</span>
              <input
                type="text"
                name="degree"
                value={form.degree}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. BSc, MD, DO"
              />
            </label>

            <label className="block">
              <span className="text-sm text-gray-700">Field of Study</span>
              <input
                type="text"
                name="fieldOfStudy"
                value={form.fieldOfStudy}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. Medicine"
              />
            </label>

            <label className="block">
              <span className="text-sm text-gray-700">Start Year</span>
              <input
                type="number"
                name="startYear"
                value={form.startYear}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                min="1900"
                max="2100"
                placeholder="YYYY"
              />
            </label>

            <label className="block">
              <span className="text-sm text-gray-700">End Year</span>
              <input
                type="number"
                name="endYear"
                value={form.endYear}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                min="1900"
                max="2100"
                placeholder="YYYY"
              />
            </label>
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
